import { useMemo, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_TYPES = [
  { k: "market", l: "Market" },
  { k: "limit", l: "Limit" },
  { k: "stop", l: "Stop-Loss" },
  { k: "take_profit", l: "Take-Profit" },
];
const LEVERAGES = [1, 5, 10, 20];

const fmt = (n, dp = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export default function AdvancedTicket({ token, coins, onTraded }) {
  const [symbol, setSymbol] = useState("BTC");
  const [orderType, setOrderType] = useState("market");
  const [qty, setQty] = useState("");
  const [usd, setUsd] = useState("");
  const [limitPrice, setLimitPrice] = useState("");
  const [leverage, setLeverage] = useState(1);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const price = useMemo(() => {
    const c = (coins || []).find((x) => x.symbol.toUpperCase() === symbol);
    return c?.price || null;
  }, [coins, symbol]);

  const effQty = qty ? parseFloat(qty) : usd && price ? parseFloat(usd) / price : 0;
  const notional = effQty && price ? effQty * price : 0;
  const margin = notional / leverage;

  const trade = async (side) => {
    setBusy(side);
    setError(null);
    setNote(null);
    try {
      const body = { symbol, side, qty: effQty, order_type: orderType, leverage };
      if (orderType !== "market") body.limit_price = parseFloat(limitPrice);
      const { data } = await axios.post(`${API}/terminal/trade`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQty("");
      setUsd("");
      setLimitPrice("");
      setNote(data.pending ? "Order placed — waiting for its trigger price." : "Filled at market price.");
      onTraded();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Trade failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border border-white/10 bg-surface p-6" data-testid="trade-ticket">
      <p className="mb-5 text-[10px] uppercase tracking-[0.25em] text-white/35">Order panel</p>

      <select
        data-testid="ticket-symbol-select"
        value={symbol}
        onChange={(e) => setSymbol(e.target.value)}
        className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white focus:border-gold focus:outline-none"
      >
        {coins.map((c) => (
          <option key={c.id} value={c.symbol.toUpperCase()}>
            {c.symbol.toUpperCase()} · ${fmt(c.price, c.price < 1 ? 4 : 2)}
          </option>
        ))}
      </select>

      <div className="mt-3 grid grid-cols-4 gap-1 border border-white/10 p-1">
        {ORDER_TYPES.map((t) => (
          <button
            key={t.k}
            data-testid={`ticket-type-${t.k}`}
            onClick={() => setOrderType(t.k)}
            className={`px-2 py-2 text-[9px] uppercase tracking-[0.1em] transition-colors duration-300 ${
              orderType === t.k ? "bg-gold text-ink" : "text-white/45 hover:text-gold"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {orderType !== "market" && (
        <input
          data-testid="ticket-limit-price"
          value={limitPrice}
          onChange={(e) => setLimitPrice(e.target.value)}
          placeholder={orderType === "limit" ? "Limit price (USD)" : "Trigger price (USD)"}
          type="number"
          min="0"
          step="any"
          className="mt-3 w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
        />
      )}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <input
          data-testid="ticket-qty-input"
          value={qty}
          onChange={(e) => { setQty(e.target.value); setUsd(""); }}
          placeholder={`Qty (${symbol})`}
          type="number"
          min="0"
          step="any"
          className="w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
        />
        <input
          data-testid="ticket-usd-input"
          value={usd}
          onChange={(e) => { setUsd(e.target.value); setQty(""); }}
          placeholder="Amount (USD)"
          type="number"
          min="0"
          step="any"
          className="w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.2em] text-white/35">Leverage</span>
        {LEVERAGES.map((l) => (
          <button
            key={l}
            data-testid={`ticket-lev-${l}`}
            onClick={() => setLeverage(l)}
            className={`flex-1 rounded-full px-2 py-1.5 font-tabular text-[10px] transition-colors duration-300 ${
              leverage === l ? "bg-gold text-ink" : "border border-white/15 text-white/45 hover:border-gold hover:text-gold"
            }`}
          >
            {l}x
          </button>
        ))}
      </div>

      <p className="mt-3 flex justify-between font-tabular text-[10px] text-white/35" data-testid="ticket-estimate">
        <span>Est. {orderType === "market" ? "price" : "trigger"}: ${fmt(orderType === "market" ? price : parseFloat(limitPrice) || null)}</span>
        <span>Margin: ${fmt(margin)}</span>
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          data-testid="ticket-buy-btn"
          onClick={() => trade("buy")}
          disabled={busy !== null || !effQty || (orderType !== "market" && !limitPrice)}
          className="rounded-xl bg-[#26A69A] px-4 py-4 text-sm uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-[#2bbbad] disabled:opacity-40"
        >
          {busy === "buy" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Buy / Long"}
        </button>
        <button
          data-testid="ticket-sell-btn"
          onClick={() => trade("sell")}
          disabled={busy !== null || !effQty || (orderType !== "market" && !limitPrice)}
          className="rounded-xl bg-[#EF5350] px-4 py-4 text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#f06060] disabled:opacity-40"
        >
          {busy === "sell" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Sell"}
        </button>
      </div>

      {error && <p className="mt-3 text-xs text-[#FF003C]" data-testid="ticket-error">{error}</p>}
      {note && !error && <p className="mt-3 text-xs text-[#26A69A]" data-testid="ticket-note">{note}</p>}
      <p className="mt-4 text-[10px] leading-relaxed text-white/30">
        Execution at live market price. Margin positions liquidate at −90% of margin.
      </p>
    </div>
  );
}
